import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Target, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { shareCtaToast } from "../lib/share";
import { useAuth } from "../lib/auth";
import { celebrateAchievement } from "../lib/achievementUX";

const OPTIONS = [
  {
    key: "accessible",
    title: "Easy & dog-safe",
    desc: "Flat, wide paths. No stiles or barriers. Great for any pack.",
    bg: "bg-brand-primary",
    text: "text-brand-sand",
  },
  {
    key: "difficult",
    title: "Some challenges",
    desc: "Stiles, gates, uneven terrain, or steep climbs. Some dogs may need lifting.",
    bg: "bg-[#D19C4C]",
    text: "text-white",
  },
  {
    key: "restricted",
    title: "Not dog-friendly",
    desc: "Dogs not permitted, or heavy restrictions (livestock, wildlife, cliffs).",
    bg: "bg-brand-error",
    text: "text-white",
  },
];

export default function ScoutRatingModal({ open, onClose, locationId, locationName, onResolved }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/locations/${locationId}/scout-rating`);
        if (!cancelled) {
          setSummary(r.data);
          if (r.data.my_vote) setChoice(r.data.my_vote);
        }
      } catch { /* summary is nice-to-have */ }
    })();
    return () => { cancelled = true; };
  }, [open, locationId, user]);

  if (!open) return null;

  async function submit() {
    if (!choice) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/locations/${locationId}/scout-rating`, {
        difficulty: choice,
        notes: notes.trim() || null,
      });
      const d = r.data;
      // Post-submission share CTA — same URL/flow as the main "Share trail"
      // button, attributed via source so telemetry can split organic shares
      // from post-contribution ones.
      const shareCta = {
        url: `${window.location.origin}/api/og/location/${locationId}`,
        title: "Check out this trail on TrailDog!",
        text: `I just found this great dog-friendly trail on TrailDog: ${locationName || "a great walk"}. Check it out here!`,
        event: "share_trail",
        props: { trail_id: locationId, source: "post_scout_rating" },
      };
      if (d.resolved) {
        shareCtaToast({
          ...shareCta,
          description: `Trail resolved as "${d.resolved_bucket}" — every walker sees your score now.`,
        });
        onResolved?.(d.resolved_bucket);
      } else {
        const need = (summary?.resolved_threshold || 3) - d.vote_count;
        shareCtaToast({
          ...shareCta,
          description:
            need > 0
              ? `Vote in — ${need} more walker${need === 1 ? "" : "s"} needed to resolve this trail.`
              : "Vote saved.",
        });
      }
      // Norse tier — trail_scout / master_scout milestones. The
      // celebrateAchievement toast points to the Trophy Wall so walkers
      // discover their new rune without having to hunt for it.
      celebrateAchievement(navigate, d.new_achievement);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your vote.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Backdrop — separate fixed layer so clicks outside the modal
          still close it, but the modal itself owns its own viewport
          anchoring so mobile browsers can't override its height. */}
      <div
        className="fixed inset-0 overflow-hidden z-[999] bg-brand-text/50 backdrop-blur-sm"
        data-testid="scout-rating-backdrop"
        onClick={onClose}
      />
      <div
        // Mobile: pinned into safe viewport space with explicit top/bottom
        // insets so the button never lands under the OS chrome or app
        // bottom-nav. Desktop (md+): reverts to a centred card.
        className="fixed inset-x-4 top-2 bottom-12 z-[1000] flex flex-col overflow-hidden md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:h-auto md:max-h-[80vh] bg-brand-sand rounded-3xl shadow-2xl border border-brand-border/60"
        data-testid="scout-rating-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — fixed at top */}
        <div className="p-5 pb-3 border-b border-brand-border/50 flex items-start gap-3 shrink-0">
          <div className="w-10 h-10 rounded-full bg-[#F5B841] flex items-center justify-center shrink-0">
            <Target size={18} className="text-[#2C3A2E]" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl text-brand-text leading-tight">
              Scout this trail
            </h3>
            <p className="text-sm text-brand-subtle mt-0.5">
              Help other walkers by rating the trail difficulty.
            </p>
          </div>
          <button
            data-testid="scout-rating-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-brand-border/40 flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <X size={18} className="text-brand-subtle" />
          </button>
        </div>

        {/* Not signed in */}
        {!user && (
          <div className="p-5" data-testid="scout-rating-signin-gate">
            <p className="text-sm text-brand-text">
              Sign in to record your scout rating. Your vote helps every
              walker who opens this trail next.
            </p>
            <button
              onClick={onClose}
              className="mt-4 w-full py-3 rounded-2xl bg-brand-primary text-brand-sand font-medium"
            >
              Close
            </button>
          </div>
        )}

        {/* Signed in — scrolling middle + shrink-0 footer. */}
        {user && (
          <>
            <div
              className="flex-1 overflow-y-auto p-5 space-y-3"
              data-testid="scout-rating-form"
            >
              {summary && summary.vote_count > 0 && (
                <div className="text-xs text-brand-subtle" data-testid="scout-rating-summary">
                  {summary.vote_count} walker{summary.vote_count === 1 ? "" : "s"} already voted
                  {summary.my_vote && ` · your current vote: ${summary.my_vote}`}
                </div>
              )}
              {OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`scout-rating-option-${opt.key}`}
                  onClick={() => setChoice(opt.key)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all ${
                    choice === opt.key
                      ? `${opt.bg} ${opt.text} border-transparent shadow-md`
                      : "bg-brand-surface border-brand-border/60 hover:border-brand-primary/40"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className={`font-heading text-base leading-tight ${choice === opt.key ? "" : "text-brand-text"}`}>
                        {opt.title}
                      </div>
                      <div className={`text-xs mt-1 ${choice === opt.key ? "opacity-90" : "text-brand-subtle"}`}>
                        {opt.desc}
                      </div>
                    </div>
                    {choice === opt.key && (
                      <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                    )}
                  </div>
                </button>
              ))}

              <label className="block">
                <span className="td-section-label">Notes (optional)</span>
                <textarea
                  data-testid="scout-rating-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                  placeholder="Muddy after rain, stile at mile 1, sheep in field…"
                  rows={2}
                  className="mt-1 w-full p-3 rounded-2xl bg-brand-surface border border-brand-border/60 text-sm text-brand-text focus:outline-none focus:border-brand-primary/60"
                />
              </label>
            </div>

            {/* Footer — shrink-0 so it never collapses; sits below the
                scroll region and above the mobile bottom-nav. */}
            <div
              className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-brand-sand border-t border-brand-border/50"
              data-testid="scout-rating-footer"
            >
              <button
                data-testid="scout-rating-submit"
                onClick={submit}
                disabled={!choice || submitting}
                className="w-full py-3 rounded-2xl bg-brand-primary text-brand-sand font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {summary?.my_vote ? "Update my vote" : "Submit scout rating"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
